import React, { Suspense, lazy, useState } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import { AuthProvider } from './context/AuthContext';
import { AnimationProvider } from './context/AnimationContext';
import ProtectedRoute from './components/ProtectedRoute';
import Header from './components/Header';
import Footer from './components/Footer';
import CasualCTA from './components/CasualCTA';
import ScrollToTop from './components/ScrollToTop';
import SplashScreen from './components/SplashScreen/SplashScreen';
import LoadingAnimation from './components/LoadingAnimation/LoadingAnimation';
import HomePage from './pages/HomePage';

// Lazy load pages for better performance
const AboutPage = lazy(() => import('./pages/AboutPage'));
const CulturePage = lazy(() => import('./pages/CulturePage'));
const JobsPage = lazy(() => import('./pages/JobsPage'));
const PeoplePage = lazy(() => import('./pages/PeoplePage'));
const InterviewListPage = lazy(() => import('./pages/InterviewListPage'));
const InterviewDetailPage = lazy(() => import('./pages/InterviewDetailPage'));
const WorkstylePage = lazy(() => import('./pages/WorkstylePage'));
const ProcessPage = lazy(() => import('./pages/ProcessPage'));
const NewGraduatePage = lazy(() => import('./pages/NewGraduatePage'));
const NewGraduateAboutPage = lazy(() => import('./pages/NewGraduateAboutPage'));
const NewGraduateCulturePage = lazy(() => import('./pages/NewGraduateCulturePage'));
const NewGraduateJobsPage = lazy(() => import('./pages/NewGraduateJobsPage'));
const NewGraduateWorkstylePage = lazy(() => import('./pages/NewGraduateWorkstylePage'));
const NewGraduateProcessPage = lazy(() => import('./pages/NewGraduateProcessPage'));
const FirestoreTest = lazy(() => import('./pages/FirestoreTest'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const LoginPage = lazy(() => import('./pages/admin/LoginPage'));
const PageEditor = lazy(() => import('./pages/admin/PageEditor'));
const PagesListPage = lazy(() => import('./pages/admin/PagesListPage'));
const SeedDataPage = lazy(() => import('./pages/admin/SeedDataPage'));
const SNSContentPage = lazy(() => import('./pages/admin/SNSContentPage'));
const ImageManagerPage = lazy(() => import('./pages/admin/ImageManagerPage'));
const InterviewsAdminPage = lazy(() => import('./pages/admin/InterviewsAdminPage'));
const InterviewEditor = lazy(() => import('./pages/admin/InterviewEditor'));
const CmsPreviewPage = lazy(() => import('./pages/admin/CmsPreviewPage'));
const DynamicPage = lazy(() => import('./pages/DynamicPage'));
const PagesDebug = lazy(() => import('./pages/PagesDebug'));
const FirestoreDebug = lazy(() => import('./pages/FirestoreDebug'));
// Contact pages removed - using mailto links as specified by Takagi-san
// const ContactPage = lazy(() => import('./pages/ContactPage'));
// const ContactCasualPage = lazy(() => import('./pages/ContactCasualPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const SecurityPolicyPage = lazy(() => import('./pages/SecurityPolicyPage'));

// Loading fallback component
const PageLoader = () => (
  <div style={{ 
    minHeight: '100vh', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center'
  }}>
    <LoadingAnimation variant="spark-orbit" size="medium" />
  </div>
);

function App() {
  const location = useLocation();
  const isCmsPath = location.pathname.startsWith('/admin') || location.pathname === '/preview';
  const [showSplash, setShowSplash] = useState(() => !isCmsPath);

  // CasualCTA shown on all pages except admin pages
  const shouldShowCasualCTA = !location.pathname.startsWith('/admin');

  return (
    <AuthProvider>
      <AnimationProvider>
        {showSplash && <SplashScreen onComplete={() => setShowSplash(false)} />}
        <div className="App">
          <a href="#main-content" className="skip-link">メインコンテンツへスキップ</a>
          <ScrollToTop />
          <Header />
        <main id="main-content" role="main">
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/culture" element={<CulturePage />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/people" element={<PeoplePage />} />
          <Route path="/interview" element={<InterviewListPage />} />
          <Route path="/interview/:slug" element={<InterviewDetailPage />} />
          <Route path="/workstyle" element={<WorkstylePage />} />
          <Route path="/process" element={<ProcessPage />} />
          <Route path="/newgraduate" element={<NewGraduatePage />} />
          <Route path="/newgraduate/about" element={<NewGraduateAboutPage />} />
          <Route path="/newgraduate/culture" element={<NewGraduateCulturePage />} />
          <Route path="/newgraduate/jobs" element={<NewGraduateJobsPage />} />
          <Route path="/newgraduate/workstyle" element={<NewGraduateWorkstylePage />} />
          <Route path="/newgraduate/process" element={<NewGraduateProcessPage />} />
          {/* Debug routes — production ビルドでは登録しない（本番から閲覧不可） */}
          {process.env.NODE_ENV !== 'production' && (
            <>
              <Route path="/firestore-test" element={<FirestoreTest />} />
              <Route path="/pages-debug" element={<PagesDebug />} />
              <Route path="/firestore-debug" element={<FirestoreDebug />} />
            </>
          )}
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<LoginPage />} />
          <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="/admin/pages" element={<ProtectedRoute><PagesListPage /></ProtectedRoute>} />
          <Route path="/admin/pages/new" element={<ProtectedRoute><PageEditor /></ProtectedRoute>} />
          <Route path="/admin/pages/edit/:id" element={<ProtectedRoute><PageEditor /></ProtectedRoute>} />
          <Route path="/admin/seed-data" element={<ProtectedRoute><SeedDataPage /></ProtectedRoute>} />
          <Route path="/admin/sns-content" element={<ProtectedRoute><SNSContentPage /></ProtectedRoute>} />
          <Route path="/admin/images" element={<ProtectedRoute><ImageManagerPage /></ProtectedRoute>} />
          <Route path="/admin/interviews" element={<ProtectedRoute><InterviewsAdminPage /></ProtectedRoute>} />
          <Route path="/admin/interviews/new" element={<ProtectedRoute><InterviewEditor /></ProtectedRoute>} />
          <Route path="/admin/interviews/edit/:slug" element={<ProtectedRoute><InterviewEditor /></ProtectedRoute>} />
          <Route path="/preview" element={<ProtectedRoute><CmsPreviewPage /></ProtectedRoute>} />
          
          {/* Contact Routes - Redirected to homepage (using mailto links now) */}
          <Route path="/contact" element={<Navigate to="/" replace />} />
          <Route path="/contact/casual" element={<Navigate to="/" replace />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/security-policy" element={<SecurityPolicyPage />} />
          
          {/* Catch-all route for CMS pages - must be last */}
          <Route path="*" element={<DynamicPage />} />
        </Routes>
        </Suspense>
        </main>
        {shouldShowCasualCTA && <CasualCTA />}
        <Footer />
      </div>
      </AnimationProvider>
    </AuthProvider>
  );
}

export default App;
